import { loadStripe } from '@stripe/stripe-js';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';

import type { AbilityTuple, MongoAbility, MongoQuery } from '@casl/ability';
import { ProtectedRoute } from './components/Routes';
import { AppContext } from './modules/context/appContext';
import { UserPropertiesContext } from './modules/context/userPropertiesContext';
import './App.css';
import { AddonsRevenueReports } from './components/Dashboards/AddonsRevenueReports';
import { Articles } from './components/Dashboards/Articles';
import { Category } from './components/Dashboards/Categories';
import { InboxDashboard } from './components/Dashboards/Inbox';
import { Packages } from './components/Dashboards/Packages';
import { PackagesView } from './components/Dashboards/Packages/PackagesView';
import { Properties } from './components/Dashboards/Properties';
import { Reports } from './components/Dashboards/Reports';
import { ReservationDetails } from './components/Dashboards/ReservationDetails';
import { ReservationsDashboard } from './components/Dashboards/Reservations';
import { ReservationsReports } from './components/Dashboards/ReservationsReports';
import { RevenueReports } from './components/Dashboards/RevenueReports';
import { RoleManagement } from './components/Dashboards/RoleManagement';
import { RoomRevenueReports } from './components/Dashboards/RoomRevenueReports';
import { Rooms } from './components/Dashboards/Rooms';
import { TemporaryAccessCode } from './components/Dashboards/TemporaryAccessCodes';
import { UserManagement } from './components/Dashboards/UserManagement';
import { AccepInvite } from './components/Forms/AcceptInvite';
import { ArticlesDetails } from './components/Forms/Articles/Details';
import { UserArticles } from './components/Forms/Articles/UserArticles';
import { CreateNewPassword } from './components/Forms/CreateNewPassword';
import { ForgotPassword } from './components/Forms/ForgotPassword';
import { Login } from './components/Forms/Login';
import GuestPageLayoutV2 from './components/GuestPagesV2/GuestPageLayout';
import { Layout } from './components/Layout';
import { AllUsersProvider } from './components/Providers/AllUsers';
import { ArticlesProvider } from './components/Providers/Articles';
import { ReservationPackageProvider } from './components/Providers/ReservationPackage';
import { UserProvider } from './components/Providers/UserProperties';
import { ProtectedRouteByProperty } from './components/Routes/protectedByProperty';
import { ProtectedRouteByRoles } from './components/Routes/protectedByRoles';
import { config } from './modules/config';
import { useAuth, usePropertyConfig } from './modules/hooks';
import { routes } from './modules/mappers/urls';
import { defineAbilityFor } from './modules/utils/abilities';
import { AbilityContext } from './modules/utils/can';
import { normalizeRoutes } from './modules/utils/ignoredRoutes';

function App() {
  const { permissions, permissionsLoading } = useAuth() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProperty } = useContext(UserPropertiesContext) || {};

  // Fetch property config to get property-specific Stripe public key
  const propertyId = selectedProperty?.property?.uuid;
  const { data: propertyConfig } = usePropertyConfig(propertyId);

  // Determine which Stripe public key to use
  // Priority: 1) Property config, 2) Environment variable fallback
  const stripePublicKey = useMemo(() => {
    return propertyConfig?.stripe?.publicKey || config.stripe.publicKey;
  }, [propertyConfig?.stripe?.publicKey]);

  // Create Stripe promise with the appropriate public key
  const stripePromise = useMemo(() => {
    if (!stripePublicKey) {
      return Promise.resolve(null);
    }
    return loadStripe(stripePublicKey);
  }, [stripePublicKey]);

  const [ability, setAbility] = useState<MongoAbility<AbilityTuple, MongoQuery>>(
    defineAbilityFor([]),
  );

  const [abilityReady, setAbilityReady] = useState(false);

  useEffect(() => {
    if (!permissions?.length) return;
    setAbility(defineAbilityFor(permissions));

    setAbilityReady(true);
  }, [permissions]);

  useEffect(() => {
    if (permissionsLoading || !abilityReady || !permissions?.length) return;
    if (!location.state) return;

    const from = (location.state as any)?.from;
    if (!from) return;

    const [, targetComponent] = from.pathname.split('/');

    if (!targetComponent) return;

    if (ability.can('view', normalizeRoutes(targetComponent))) {
      const targetUrl = `${from.pathname}${from.search || ''}`;
      if (location.pathname + location.search !== targetUrl) {
        navigate(targetUrl, { replace: true, state: {} });
      } else {
        navigate(location.pathname + location.search, {
          replace: true,
          state: {},
        });
      }
    } else {
      navigate(location.pathname + location.search, {
        replace: true,
        state: {},
      });
    }
  }, [permissionsLoading, abilityReady, location, navigate]);

  const initCtx = useMemo(() => {
    return { stripePromise };
  }, [stripePromise]);

  return (
    <AppContext.Provider value={initCtx}>
      <Routes>
        <Route path={routes.LOGIN} element={<Login />} />
        <Route path={routes.FORGOTPASSWORD} element={<ForgotPassword />} />
        <Route path={routes.CREATENEWPASSWORD} element={<CreateNewPassword />} />
        <Route path={routes.ACCEPT_INVITE} element={<AccepInvite />} />
        <Route
          path={routes.HOME}
          element={
            <AbilityContext.Provider value={ability}>
              <ProtectedRoute>
                <UserProvider>
                  <AllUsersProvider>
                    <ArticlesProvider>
                      <ReservationPackageProvider>
                        <Layout />
                      </ReservationPackageProvider>
                    </ArticlesProvider>
                  </AllUsersProvider>
                </UserProvider>
              </ProtectedRoute>
            </AbilityContext.Provider>
          }
        >
          <Route
            path={routes.PROPERTIES}
            element={
              <ProtectedRouteByRoles component="properties">
                <Properties />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.ROOMS}
            element={
              <ProtectedRouteByRoles component="rooms">
                <Rooms />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.RESERVATIONS}
            element={
              <ProtectedRouteByProperty>
                <ProtectedRouteByRoles component="reservations">
                  <ReservationsDashboard />
                </ProtectedRouteByRoles>
              </ProtectedRouteByProperty>
            }
          />
          <Route
            path={routes.GROUP_BOOKINGS}
            element={
              <ProtectedRouteByProperty>
                <ProtectedRouteByRoles component="group_bookings">
                  <ReservationsDashboard fromGrouping />
                </ProtectedRouteByRoles>
              </ProtectedRouteByProperty>
            }
          />
          <Route
            path={routes.RESERVATION_DETAILS}
            element={
              <ProtectedRouteByRoles component="reservations">
                <ReservationDetails />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.INBOX}
            element={
              <ProtectedRouteByProperty>
                <ProtectedRouteByRoles component="inbox">
                  <InboxDashboard />
                </ProtectedRouteByRoles>
              </ProtectedRouteByProperty>
            }
          />
          <Route
            path={routes.ARTICLES}
            element={
              <ProtectedRouteByRoles component="articles">
                <Articles />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.CATEGORIES}
            element={
              <ProtectedRouteByRoles component="categories">
                <Category />
              </ProtectedRouteByRoles>
            }
          />

          <Route
            path={routes.ARTICLES_DETAILS}
            element={
              <ProtectedRouteByRoles component="articles_dashboard">
                <ArticlesDetails subpage={false} />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.USERS_ARTICLES}
            element={
              <ProtectedRouteByRoles component="articles_dashboard">
                <UserArticles />
              </ProtectedRouteByRoles>
            }
          />

          <Route
            path={routes.PACKAGES}
            element={
              <ProtectedRouteByProperty>
                <ProtectedRouteByRoles component="packages">
                  <Packages />
                </ProtectedRouteByRoles>
              </ProtectedRouteByProperty>
            }
          />
          <Route
            path={routes.PACKAGES_MANAGEMENT}
            element={
              <ProtectedRouteByRoles component="packages_management">
                <PackagesView />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.REPORTS}
            element={
              <ProtectedRouteByRoles component="occupancy_reports">
                <Reports />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.REVENUEREPORTS}
            element={
              <ProtectedRouteByRoles component="revenue_reports">
                <RevenueReports />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.ROOMREVENUEREPORTS}
            element={
              <ProtectedRouteByRoles component="room_revenue_reports">
                <RoomRevenueReports />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.ADDONSREVENUEREPORTS}
            element={
              <ProtectedRouteByRoles component="addons_revenue_reports">
                <AddonsRevenueReports />
              </ProtectedRouteByRoles>
            }
          />

          <Route
            path={routes.RESERVATIONSREPORTS}
            element={
              <ProtectedRouteByRoles component="reservations_reports">
                <ReservationsReports />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.USERS_MANAGEMENT}
            element={
              <ProtectedRouteByRoles component="users">
                <UserManagement />
              </ProtectedRouteByRoles>
            }
          />

          <Route
            path={routes.TEMPORAL_ACCESS_CODES}
            element={
              <ProtectedRouteByRoles component="rooms">
                <TemporaryAccessCode />
              </ProtectedRouteByRoles>
            }
          />
          <Route
            path={routes.ROLE_MANAGEMENT}
            element={
              <ProtectedRouteByRoles component="roles">
                <RoleManagement />
              </ProtectedRouteByRoles>
            }
          />
        </Route>
        <Route path={routes.GUEST_PORTAL_V2} element={<GuestPageLayoutV2 />} />
        <Route path={routes.GUEST_REGISTRATION_V2} element={<GuestPageLayoutV2 />} />
        <Route path={routes.FEEDBACK_V2} element={<GuestPageLayoutV2 />} />

        <Route path="*" element={<Login />} />
      </Routes>
    </AppContext.Provider>
  );
}

export default App;
